import * as Sentry from '@sentry/browser';
import { Middleware } from 'ng-redux';
import Redux, { Action } from 'redux';

import { AppThunk, ICuralateStore } from '@models/AppState';

/**
 * Custom middleware that will send any unhandled errors from thunks to Sentry. Loosely
 * copied from https://bionic.fullstory.com/react-redux-errors-with-fullstory-and-sentry/
 */
export const loggerReduxMiddleware: Middleware =
  () =>
  (next: <T>(action: Action | AppThunk<T>) => unknown) =>
  <T>(action: Action | AppThunk<T>) => {
    // The next action is a thunk, so prep it to be handled by our redux-thunk middleware
    if (typeof action === 'function') {
      // Give it a try/catch wrapper so we hear about any failures downstream
      const wrapAction =
        (fn: AppThunk<T>) =>
        (
          dispatch: Redux.Dispatch,
          getState: () => ICuralateStore,
          extraArgument: unknown,
        ) => {
          try {
            return fn(dispatch, getState, extraArgument);
          } catch (e) {
            Sentry.captureException(e);
          }
        };
      // Pass the wrapped action to redux-thunk to handle.
      return next(wrapAction(action));
    }

    // The next action isn't a thunk, so place it a try/catch so we hear about reducer failures
    try {
      return next(action);
    } catch (e) {
      Sentry.captureException(e);
    }
  };
